import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Cpu, Layers, MessageSquare, Radio } from 'lucide-react';
import Partners from './mkt/ui/Partners';
import CtaBand from './mkt/ui/CtaBand';
import Seo from './mkt/ui/Seo';

const PARTICIPANTS = [
  {
    title: 'Agents',
    sub: 'Interpret what equipment reports, beside the equipment itself',
    path: '/product',
    Icon: Cpu,
  },
  {
    title: 'Devices',
    sub: 'Exchange context directly, without a central system in between',
    path: '/product',
    Icon: Radio,
  },
  {
    title: 'People',
    sub: 'Reached on a channel they already use — and able to answer back',
    path: '/product',
    Icon: MessageSquare,
  },
];

const Home = () => {
  const navigate = useNavigate();

  return (
    <div>
      <Seo
        path="/"
        description="A communication platform for machines and the people who depend on them. Agents at the edge decide what matters, devices share it with each other, and the right person hears about it on the channel they read."
      />
      {/* Hero */}
      <section className="cp-hero">
        <div className="cp-container">
          <div>
            <span className="cp-eyebrow">
              <Cpu size={12} style={{ verticalAlign: '-1px', marginRight: 6 }} />
              Machines and people, one conversation
            </span>
            <h1 className="cp-h1">
              Machines that can tell you something.
            </h1>
            <p className="cp-lede">
              CoPlaid connects equipment to the people who depend on it. An agent at the edge
              decides whether what a machine is reporting matters, tells the machines around it,
              and reaches whoever needs to act — on the channel they actually read, with replies
              that come back and do something.
            </p>
            <div className="cp-hero-actions">
              <button className="cp-btn cp-btn--primary cp-btn--lg" onClick={() => navigate('/partners')}>
                For partners <ArrowRight size={18} />
              </button>
              <button className="cp-btn cp-btn--ghost cp-btn--lg" onClick={() => navigate('/product')}>
                See it in action
              </button>
            </div>
            <div className="cp-hero-meta">
              <span>Delivered by <strong>implementation partners.</strong></span>
              <span>Under <strong>your brand.</strong></span>
            </div>
          </div>
        </div>
      </section>

      {/* Partners strip */}
      <div className="cp-container">
        <Partners />
      </div>

      {/* Products */}
      <section className="cp-section cp-section--surface">
        <div className="cp-container">
          <div style={{ maxWidth: 720, margin: '0 auto 48px', textAlign: 'center' }}>
            <span className="cp-eyebrow">Products</span>
            <h2 className="cp-h2" style={{ marginTop: 16 }}>Three participants, one conversation.</h2>
            <p className="cp-lede" style={{ margin: '0 auto' }}>
              Machines that can say something, and people who can be reached and answer back —
              with whatever runs your work sitting in the middle of it.
            </p>
          </div>
          <div className="cp-grid-2">
            {[{ group: '', blurb: '', items: PARTICIPANTS }].map((group) => (
              <div className="cp-product-group" key={group.group}>
                <div className="cp-product-group__group">{group.group}</div>
                <h3 className="cp-product-group__title">{group.blurb}</h3>
                <ul className="cp-product-group__list">
                  {group.items.map(({ title, sub, path, Icon, status }) => (
                    <li key={path}>
                      <a
                        className="cp-product-link"
                        href={path}
                        onClick={(e) => { e.preventDefault(); navigate(path); }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{
                            width: 36, height: 36, borderRadius: 8,
                            background: 'var(--brand-50)', color: 'var(--brand-600)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <Icon size={18} />
                          </span>
                          <span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              {title}
                            </span>
                            <span>{sub}</span>
                          </span>
                        </span>
                        <ArrowRight size={18} className="cp-product-link-arrow" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why CoPlaid */}
      <section className="cp-section">
        <div className="cp-container">
          <div style={{ maxWidth: 720, marginBottom: 48 }}>
            <span className="cp-eyebrow">Why CoPlaid</span>
            <h2 className="cp-h2" style={{ marginTop: 16 }}>Built for the way operations actually work.</h2>
          </div>
          <div className="cp-grid-3">
            <div className="cp-card">
              <div className="cp-card-icon"><Cpu size={22} /></div>
              <h3 className="cp-card-title">Judgement, not another dashboard</h3>
              <p className="cp-card-text">
                A signal that becomes an indicator on a screen nobody is watching has not
                communicated anything. The measure is whether the right person acted, not
                whether the data was captured.
              </p>
            </div>
            <div className="cp-card">
              <div className="cp-card-icon"><MessageSquare size={22} /></div>
              <h3 className="cp-card-title">Whoever needs to know</h3>
              <p className="cp-card-text">
                Customers, technicians, duty managers — reached on a channel they already use,
                from one workflow, with consent capture and registration handled centrally.
              </p>
            </div>
            <div className="cp-card">
              <div className="cp-card-icon"><Layers size={22} /></div>
              <h3 className="cp-card-title">It arrives where the work is</h3>
              <p className="cp-card-text">
                A conclusion only counts if it reaches the system your teams already work in.
                Direct integrations where they exist, authenticated APIs everywhere else — not
                another place for anyone to check.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cp-section">
        <div className="cp-container">
          <CtaBand
            title="Do you implement the systems your clients run on?"
            sub="CoPlaid reaches customers through the consultants and implementation teams who are already inside those systems. Bring us your client base and we’ll tell you honestly whether it fits."
            primary={{ label: 'Become a partner', to: '/partners' }}
            secondary={{ label: 'Talk to us', to: '/contact' }}
          />
        </div>
      </section>
    </div>
  );
};

export default Home;
